import React, {
  createContext,
  useReducer,
  useContext,
  useMemo,
  useCallback,
  useEffect,
} from "react";

import {
  LOADING_STATES,
  CUSTOM_PANEL_DEFAULT_OPERATOR,
  GEOGRAPHICAL_REGIONS_CONFIG,
  LOCATION_FILTER_ACTIONS,
  PLACEMENT_FILTERS,
  SKILL_TYPES,
  USER_TYPES,
  COMMUNITY_BOOST_LEVELS,
} from "lookup";

import { cloneDeep, debounce, difference, union } from "lodash";

import { contextConstants } from "context/constants";
import { contextActions } from "context/actions";
import { contextReducers } from "context/reducers";
import { JobsContext } from "./JobsProvider";
import { AlertContext } from "./AlertProvider";
import { getDefaultExcludeBHStatusFilterStr } from "utils/helpers/bullhorn";
import {
  abortCurrentStreamSearch,
  formatAISkills,
  formatAISkillSearchString,
} from "utils/helpers/search";
import { TABS } from "context/constants/common";
import { getFilteredUserIDs } from "utils/helpers/search";
import { AuthContext } from "./AuthProvider";

export const SearchContext = createContext();

const FILTER_SEARCH_DEBOUNCE_MS = 650;

export const SearchProvider = ({ children }) => {
  const { jobOpp } = useContext(JobsContext);
  const { addCustomAlert, addGraphQLAlert } = useContext(AlertContext);
  const { user: loggedInUser } = useContext(AuthContext);

  const [state, dispatch] = useReducer(
    contextReducers.search.reducer,
    contextReducers.search.initialState
  );

  const geographicalRegions = GEOGRAPHICAL_REGIONS_CONFIG;

  const dispatchReadyForSearch = useCallback((value) => {
    dispatch({
      type: contextConstants.search.READY_FOR_SEARCH_CHANGED,
      payload: { value },
    });
  }, []);

  const debouncedFilterSearch = useMemo(
    () =>
      debounce(() => {
        dispatchReadyForSearch(true);
      }, FILTER_SEARCH_DEBOUNCE_MS),
    [dispatchReadyForSearch]
  );

  const scheduleFilterDrivenSearch = () => {
    abortCurrentStreamSearch();
    debouncedFilterSearch.cancel();
    dispatchReadyForSearch(false);
    debouncedFilterSearch();
  };

  useEffect(() => {
    return () => {
      debouncedFilterSearch.cancel();
    };
  }, [debouncedFilterSearch]);

  const getDefaultCommunityBoost = () => {
    if (loggedInUser?.canAccessCommunityBoost) {
      return COMMUNITY_BOOST_LEVELS.MEDIUM;
    }

    return COMMUNITY_BOOST_LEVELS.NONE;
  };

  const defaultFilters = useMemo(
    () => {
      return {
        customSearchQuery: "",
        previousAdvanceSearchStateStr: "{}",
        basicSearch: false,
        range: {},
        toggle: { agreedToTerms: false },
        refinementList: {
          userType: [USER_TYPES.FREELANCER],
        },
        communityBoost: COMMUNITY_BOOST_LEVELS.NONE,
        multiRange: {},
        locationFilterStr: "",
        locationFilter: {},
        configProps: {},
        placementFilterStr: PLACEMENT_FILTERS.notHasActivePlacements,
        yearsOfExperienceFilterStr: "",
        BHStatusFilterStr: "",
        TKSkillsHierarchyExp: [],
        skills: {
          required: [],
          optional: [],
          reasoning: "",
          aiSkills: [],
        },
        skillsStr: [],
        skillsStrFilter: "",
        page: 1,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [jobOpp?.id]
  );

  const _getHashQueryParams = () => {
    const hash = window.location.hash;
    const queryStart = hash.indexOf("?");
    const queryString = queryStart !== -1 ? hash.slice(queryStart + 1) : "";

    const searchParams = new URLSearchParams(queryString);
    const params = {};

    for (const [key, value] of searchParams.entries()) {
      params[key] = value;
    }

    return params;
  };

  const setSearchState = (newState, isUnmounting) => {
    if (!newState) return;

    const queryChanged =
      newState.customSearchQuery !== state.searchState.customSearchQuery;
    const refinementsChanged =
      JSON.stringify(state.searchState.refinementList) !==
      JSON.stringify(newState.refinementList);

    const shouldResetPage = queryChanged || refinementsChanged;

    const payload = {
      searchState: {
        ...newState,
        page: shouldResetPage
          ? 1
          : newState?.page ?? state.searchState.page ?? 1,
      },
    };

    dispatch({
      type: contextConstants.search.SEARCH_STATE_CHANGED,
      payload,
    });

    const params = _getHashQueryParams();

    const tab = params.tab;

    const isSearchResultsTab = !tab || tab === TABS.SEARCHRESULTS;

    // setting value to null will allow us to completely block api request until
    // manually changed
    if (isUnmounting || state.isReadyForSearch === null) {
      return;
    }

    if (state.pageIsLoaded && isSearchResultsTab) {
      scheduleFilterDrivenSearch();
    }
  };

  const setSkillSearchState = (skills) => {
    if (!skills) return;

    // Every action of the user w.r.t search agent - such as reset, clear, add, remove
    // and disable needs to be saved to local storage. This function is called for each of
    // the mentioned actions, and thus is the best spot to save to storage
    _saveSkillsToLocalStorage({
      requiredSkills: skills.required,
      optionalSkills: skills.optional,
      reasoning: skills.reasoning,
    });

    dispatch({
      type: contextConstants.search.SKILL_SEARCH_STATE_CHANGED,
      payload: { skills },
    });
  };

  const handleReRankHitsForJob = (value, fieldKey) => {
    dispatch({
      type: contextConstants.search.RE_RANK_HITS_FOR_JOB_CHANGED,
      payload: {
        value,
        fieldKey,
      },
    });
  };

  const handlePageIsLoaded = (value) => {
    dispatch({
      type: contextConstants.search.PAGE_IS_LOADED_CHANGED,
      payload: { value },
    });
  };

  const handleReadyForSearch = (value) => {
    debouncedFilterSearch.cancel();
    dispatchReadyForSearch(value);
  };

  const handleNoHitsFoundOnTKSkillsHierarchy = () => {
    const TKSkillsHierarchyExpCpy = cloneDeep(
      state.searchState?.TKSkillsHierarchyExp || []
    );

    const lastFacetFilter = TKSkillsHierarchyExpCpy.pop();

    const SKILL_EXP_MAP = {
      low: "0-2y",
      medium: "2-5y",
      high: "5+y",
    };
    addCustomAlert({
      title: "Skill Experience",
      variation: "default",
      message: `No results found for: ${lastFacetFilter
        .split(" > ")
        .map((e) => SKILL_EXP_MAP[e] || e)
        .join(" ")}`,
      duration: 8000,
      callback: {
        label: "Undo",
        action: () => {
          setSearchState({
            ...state.searchState,
            TKSkillsHierarchyExp: TKSkillsHierarchyExpCpy,
          });
        },
      },
    });
  };

  const setHits = (hits) => {
    if (
      hits.length === 0 &&
      state.searchState?.TKSkillsHierarchyExp?.length > 0
    ) {
      handleNoHitsFoundOnTKSkillsHierarchy();
    }
    dispatch({
      type: contextConstants.search.HITS_LOADED,
      payload: { hits },
    });
  };

  const replaceHit = (hit) => {
    const hits = contextActions.search.replaceHit(hit, state.hits);

    setHits(hits);
  };

  const setDisjunctiveFacets = (disjunctiveFacets) => {
    dispatch({
      type: contextConstants.search.DISJUNCTIVE_FACETS_CHANGED,
      payload: { disjunctiveFacets },
    });
  };

  const updateHitField = (hitId, fieldKey, value) => {
    const updatedHits = contextActions.search.updateHitFieldByKey(
      state.hits,
      hitId,
      fieldKey,
      value
    );

    const updatedHit = updatedHits.find((hit) => hit.id === hitId);

    dispatch({
      type: contextConstants.search.HITS_LOADED,
      payload: { ...state, hits: updatedHits },
    });

    return updatedHit;
  };

  const updateHit = async (args) => {
    const updatedHit = await contextActions.search.updateHit(args);

    replaceHit(updatedHit);
  };

  const setOperators = (valueKey, trigger = true) => {
    if (Array.isArray(valueKey)) {
      const operators = {};

      valueKey.forEach(
        (el) => (operators[el] = CUSTOM_PANEL_DEFAULT_OPERATOR[el])
      );

      dispatch({
        type: contextConstants.search.OPERATOR_UPDATED,
        payload: { operators },
      });
    } else {
      dispatch({
        type: contextConstants.search.OPERATOR_UPDATED,
        payload: {
          operators: {
            ...state.operators,
            [valueKey]: state.operators[valueKey] === "and" ? "or" : "and",
          },
        },
      });
    }

    if (trigger) {
      handleReadyForSearch(true);
    }
  };

  const setHideUsersFlags = ({
    hideSkipped,
    hideRejected,
    hideMatchedAndApplied,
  }) => {
    dispatch({
      type: contextConstants.search.HIDE_SKIPPED_CHANGED,
      payload: { hideSkipped },
    });
    dispatch({
      type: contextConstants.search.HIDE_REJECTED_CHANGED,
      payload: { hideRejected },
    });
    dispatch({
      type: contextConstants.search.HIDE_MATCHED_CHANGED,
      payload: { hideMatchedAndApplied },
    });
  };

  const setHideSkipped = (hideSkipped, matches) => {
    dispatch({
      type: contextConstants.search.HIDE_SKIPPED_CHANGED,
      payload: { hideSkipped },
    });

    if (state.searchState.basicSearch) {
      return;
    }

    const finalState = {
      ...state.searchState,
      configProps: {
        ...state.searchState.configProps,
        facetsExcludes: {
          objectID: getFilteredUserIDs(
            matches,
            hideSkipped,
            state.hideRejected,
            state.hideMatchedAndApplied
          ),
        },
      },
    };

    setSearchState(finalState);
  };

  const setHideRejected = (hideRejected, matches) => {
    dispatch({
      type: contextConstants.search.HIDE_REJECTED_CHANGED,
      payload: { hideRejected },
    });

    if (state.searchState.basicSearch) {
      return;
    }

    const finalState = {
      ...state.searchState,
      configProps: {
        ...state.searchState.configProps,
        facetsExcludes: {
          objectID: getFilteredUserIDs(
            matches,
            state.hideSkipped,
            hideRejected,
            state.hideMatchedAndApplied
          ),
        },
      },
    };

    setSearchState(finalState);
  };

  const setHideMatchedAndApplied = (hideMatchedAndApplied, matches) => {
    dispatch({
      type: contextConstants.search.HIDE_MATCHED_CHANGED,
      payload: { hideMatchedAndApplied },
    });

    if (state.searchState.basicSearch) {
      return;
    }

    const finalState = {
      ...state.searchState,
      configProps: {
        ...state.searchState.configProps,
        facetsExcludes: {
          objectID: getFilteredUserIDs(
            matches,
            state.hideSkipped,
            state.hideRejected,
            hideMatchedAndApplied
          ),
        },
      },
    };

    setSearchState(finalState);
  };

  // handles location filter change within the widget
  // actions ADD/Replace REMOVE
  const locationFilterChange = (action, location) => {
    let locationFilter = cloneDeep(state.searchState.locationFilter) ?? {};

    if (action === LOCATION_FILTER_ACTIONS.ADD) {
      locationFilter = { ...location };
      refineLocationCountries([location.countryName]);
    }

    if (action === LOCATION_FILTER_ACTIONS.REMOVE) {
      contextActions.search.removeLocationFilter(locationFilter, location);

      if (!locationFilter.countryName) {
        refineLocationCountries([]);
      }
    }

    // city has been cleared - clear aroundLatLng and aroundRadius
    if (!location.cityName && locationFilter.aroundLatLng) {
      delete locationFilter.aroundLatLng;
      delete locationFilter.aroundRadius;
    }

    const locationFilterStr =
      contextActions.search.getLocationFilterStr(locationFilter);

    dispatch({
      type: contextConstants.search.LOCATION_FILTER_CHANGED,
      payload: { locationFilter, locationFilterStr },
    });
  };

  const refineLocationCountries = (countries) => {
    dispatch({
      type: contextConstants.search.COUNTRY_REFINEMENT_CHANGED,
      payload: { countries },
    });
  };

  const setGeographicalRegions = (
    geographicalRegions,
    setSearch = true,
    selectedCountries = [],
    unSelectedCountries = []
  ) => {
    dispatch({
      type: contextConstants.search.GEOGRAPHICAL_REGION_CHANGED,
      payload: { geographicalRegions },
    });

    if (setSearch) {
      let finalState;

      if (selectedCountries.length === 0) {
        let activeCountries = [];

        const existingCountries =
          state.searchState.refinementList?.["location.countryName"] || [];

        if (unSelectedCountries.length > 0) {
          activeCountries = difference(existingCountries, unSelectedCountries);
        } else {
          const activeRegions = geographicalRegions.filter(
            (region) => region.checked
          );

          const regionCountries = activeRegions
            .map(({ countryNames }) => countryNames)
            .flat();

          activeCountries = union(existingCountries, regionCountries);
        }

        finalState = {
          ...state.searchState,
          refinementList: {
            ...state.searchState.refinementList,
            "location.countryName": activeCountries,
          },
        };
      } else {
        finalState = {
          ...state.searchState,
          refinementList: {
            ...state.searchState.refinementList,
            "location.countryName": selectedCountries,
          },
        };
      }

      setSearchState(finalState);
    }
  };

  const setProximitySearch = (updatedRadius) => {
    const locationFilter = {
      ...state.searchState.locationFilter,
      aroundRadius:
        typeof updatedRadius === "number" ? Number(updatedRadius) : null,
    };

    dispatch({
      type: contextConstants.search.LOCATION_FILTER_CHANGED,
      payload: {
        locationFilter,
        locationFilterStr: state.searchState.locationFilterStr,
      },
    });
  };

  const clearLocationFilters = () => {
    setGeographicalRegions(geographicalRegions, false);

    const finalState = {
      ...state.searchState,
      locationFilter: {},
      locationFilterStr: "",
      refinementList: {
        ...state.searchState.refinementList,
        "location.countryName": [],
      },
    };

    setSearchState(finalState);
  };

  const clearSearchState = ({
    isUnMounting,
    urlParamData,
    previousAdvanceSearchStateStr,
    dontClearSearchAgentSkills,
    searchState,
  }) => {
    setGeographicalRegions(geographicalRegions, false);

    const clonedDefaultFilters = cloneDeep(defaultFilters);

    clonedDefaultFilters.communityBoost = getDefaultCommunityBoost();

    if (!isUnMounting) {
      clonedDefaultFilters.configProps = {
        ...state.searchState?.configProps,
        ...searchState?.configProps,
        optionalWords: "",
      };
    }

    if (!jobOpp.id) {
      clonedDefaultFilters.placementFilterStr = "";
    }

    if (jobOpp?.id) {
      clonedDefaultFilters.BHStatusFilterStr =
        getDefaultExcludeBHStatusFilterStr();
    }

    if (urlParamData) {
      for (let key in urlParamData) {
        clonedDefaultFilters[key] = urlParamData[key];
      }
    }

    if (previousAdvanceSearchStateStr) {
      clonedDefaultFilters.previousAdvanceSearchStateStr =
        previousAdvanceSearchStateStr;
    }

    // Ensure skills are always reset to prevent cross-job contamination
    clonedDefaultFilters.skills = {
      required: [],
      optional: [],
      reasoning: "",
      aiSkills: [],
    };

    if (dontClearSearchAgentSkills && searchState?.skills) {
      clonedDefaultFilters.skills = { ...searchState.skills };
    }

    clonedDefaultFilters.skillsStr = [];
    clonedDefaultFilters.skillsStrFilter = "";

    setSearchState(clonedDefaultFilters, isUnMounting);
    setOperators(Object.keys(state.operators), false);
  };

  const _loadSkillsFromLocalStorage = useCallback(() => {
    const skillsInStorage = localStorage.getItem("skills");

    if (!skillsInStorage) {
      return {
        allSkillsInStorage: [],
        jobOppSkills: null,
        isOutdated: false,
      };
    }

    const allSkillsInStorage = JSON.parse(skillsInStorage);

    const jobOppSkills =
      jobOpp?.id && allSkillsInStorage?.[jobOpp.id]
        ? allSkillsInStorage[jobOpp.id]
        : null;

    const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
    const now = Date.now();

    const isUpdated =
      jobOppSkills &&
      jobOppSkills?.updatedAt &&
      now - jobOppSkills?.updatedAt < ONE_MONTH_MS;

    return {
      allSkillsInStorage: allSkillsInStorage,
      jobOppSkills,
      isOutdated: !isUpdated,
    };
  }, [jobOpp?.id]);

  const _saveSkillsToLocalStorage = useCallback(
    ({ requiredSkills, optionalSkills, reasoning }) => {
      const { allSkillsInStorage } = _loadSkillsFromLocalStorage();

      const ONE_MONTH_MS = 30 * 24 * 60 * 60 * 1000;
      const now = Date.now();

      const skillsToSave = { ...allSkillsInStorage };

      const jobOppInStorage = (jobOpp?.id && skillsToSave?.[jobOpp.id]) || {};

      const updatedJobOppSkills = {
        createdAt: jobOppInStorage?.createdAt || now,
        updatedAt: now,
        requiredSkills: requiredSkills || jobOppInStorage?.requiredSkills || [],
        optionalSkills: optionalSkills || jobOppInStorage?.optionalSkills || [],
        reasoning: reasoning || jobOppInStorage?.reasoning || "",
      };

      skillsToSave[jobOpp.id] = updatedJobOppSkills;

      for (const [jobId, data] of Object.entries(skillsToSave)) {
        const updatedAt = new Date(data.updatedAt).getTime();
        if (now - updatedAt > ONE_MONTH_MS) {
          delete skillsToSave[jobId];
        }
      }

      localStorage.setItem("skills", JSON.stringify(skillsToSave));
    },
    [_loadSkillsFromLocalStorage, jobOpp?.id]
  );

  const handleAISkillLoadingState = (loadState) => {
    dispatch({
      type: contextConstants.search.LOADING_AI_SEARCH_CHANGED,
      payload: { loadingAISearch: loadState },
    });
  };

  const handleLoadAISkills = async ({
    refresh = false,
    noCache = true,
    paramsId,
    canFetch = true,
  } = {}) => {
    // Capture current job ID to detect race conditions
    const currentJobId = jobOpp?.id;

    if (!currentJobId) {
      handleAISkillLoadingState(LOADING_STATES.ERROR);
      return;
    }

    if (paramsId && paramsId !== currentJobId) {
      handleAISkillLoadingState(LOADING_STATES.ERROR);
      return;
    }

    handleAISkillLoadingState(LOADING_STATES.LOADING);

    let AISkillsForSearch = {};

    const { jobOppSkills, isOutdated } = _loadSkillsFromLocalStorage();

    const facetsExcludes = {
      facetsExcludes: {
        ...(state.searchState?.configProps?.facetsExcludes ?? {}),
      },
    };

    const newSearchState = {
      ...cloneDeep(state.searchState),
      customSearchQuery: "",
      configProps: {
        queryType: "prefixAll",
        advancedSyntax: true,
        removeWordsIfNoResults: "lastWords",
        ...facetsExcludes,
      },
      refinementList: {
        ...state.searchState.refinementList,
        activeJobTypeTitles: [],
        "careers.TKSkillsHierarchy": [],
      },
    };

    if (!refresh && !isOutdated && jobOppSkills) {
      // Verify job ID hasn't changed during localStorage access
      if (currentJobId !== jobOpp?.id) {
        handleAISkillLoadingState(LOADING_STATES.ERROR);
        return;
      }

      AISkillsForSearch = {
        requiredSkills: jobOppSkills.requiredSkills,
        optionalSkills: jobOppSkills.optionalSkills,
        reasoning: jobOppSkills?.reasoning,
      };
    } else {
      if (!canFetch) {
        handleAISkillLoadingState(LOADING_STATES.SUCCESS);

        return newSearchState;
      }

      try {
        const { data } =
          await contextActions.search.getJobOpportunityAISkillsForSearch(
            currentJobId,
            noCache
          );

        // Verify job ID hasn't changed during async API call
        if (currentJobId !== jobOpp?.id) {
          handleAISkillLoadingState(LOADING_STATES.ERROR);
          return;
        }

        const parsedResponse = JSON.parse(
          data.getJobOpportunityAISkillsForSearch
        );

        const { algolia = {}, reasoning = "" } = parsedResponse;

        AISkillsForSearch = {
          requiredSkills: formatAISkills(
            algolia.required ?? [],
            SKILL_TYPES.REQUIRED
          ),
          optionalSkills: formatAISkills(
            algolia.optional ?? [],
            SKILL_TYPES.OPTIONAL
          ),
          reasoning,
        };
      } catch (err) {
        addGraphQLAlert(err);
        handleAISkillLoadingState(LOADING_STATES.ERROR);
        return;
      }
    }

    newSearchState.optionalWords = formatAISkillSearchString(
      AISkillsForSearch?.optionalSkills
    );

    newSearchState.skills = {
      required: AISkillsForSearch?.requiredSkills,
      optional: AISkillsForSearch?.optionalSkills,
      reasoning: AISkillsForSearch?.reasoning,
      aiSkills: [
        ...AISkillsForSearch?.requiredSkills,
        ...AISkillsForSearch?.optionalSkills,
      ],
    };

    _saveSkillsToLocalStorage({
      requiredSkills: AISkillsForSearch?.requiredSkills,
      optionalSkills: AISkillsForSearch?.optionalSkills,
      reasoning: AISkillsForSearch?.reasoning,
    });

    handleAISkillLoadingState(LOADING_STATES.SUCCESS);
    return newSearchState;
  };

  const searchSkills = async (query) => {
    try {
      const response = await contextActions.search.searchTKSkills(query);
      return response.data.searchTKSkills;
    } catch (error) {
      addGraphQLAlert(error);
      return [];
    }
  };

  const handleCommunityBoosChange = (value) => {
    setSearchState({
      ...state.searchState,
      communityBoost: value,
    });
  };

  const search = {
    searchState: state.searchState,
    hits: state.hits,
    operators: state.operators,
    hideSkipped: state.hideSkipped,
    hideRejected: state.hideRejected,
    hideMatchedAndApplied: state.hideMatchedAndApplied,
    geographicalRegions: state.geographicalRegions,
    locationFilter: state.searchState.locationFilter ?? {},
    disjunctiveFacets: state.disjunctiveFacets,
    loadingAISearch: state.loadingAISearch,
    reRankHitsForJob: state.reRankHitsForJob,
    isReadyForSearch: state.isReadyForSearch,
    pageIsLoaded: state.pageIsLoaded,
    handleReadyForSearch,
    handlePageIsLoaded,
    handleReRankHitsForJob,
    setSearchState,
    setHits,
    setProximitySearch,
    updateHitField,
    updateHit,
    setOperators,
    clearSearchState,
    setHideSkipped,
    setHideRejected,
    setHideMatchedAndApplied,
    setHideUsersFlags,
    locationFilterChange,
    clearLocationFilters,
    refineLocationCountries,
    setGeographicalRegions,
    setDisjunctiveFacets,
    handleLoadAISkills,
    setSkillSearchState,
    handleAISkillLoadingState,
    searchSkills,
    handleCommunityBoosChange,
  };

  return (
    <SearchContext.Provider value={search}>{children}</SearchContext.Provider>
  );
};
